import * as React from 'react';
import type { Editor } from '@tiptap/react';

/** The mounted editor element, or null while the editor view is not mounted or has been destroyed. */
export function mountedEditorDom(editor: Editor): HTMLElement | null {
  if (editor.isDestroyed) return null;
  try {
    return editor.view.dom;
  } catch {
    // Tiptap throws for view properties until EditorContent mounts the view.
    return null;
  }
}

/**
 * Listens to a DOM event on the editor element, attaching once the view mounts so callers rendered
 * before EditorContent (for example ribbon tools) still receive events.
 */
export function useEditorDomListener<K extends keyof HTMLElementEventMap>(
  editor: Editor | null,
  type: K,
  listener: ((event: HTMLElementEventMap[K]) => void) | undefined,
  capture = false,
) {
  const current = React.useRef(listener);
  React.useEffect(() => { current.current = listener; }, [listener]);
  const active = Boolean(listener);
  React.useEffect(() => {
    if (!editor || !active) return;
    const handle = (event: HTMLElementEventMap[K]) => current.current?.(event);
    let detach = () => {};
    const attach = () => {
      detach();
      const dom = mountedEditorDom(editor);
      if (!dom) return;
      dom.addEventListener(type, handle, capture);
      detach = () => dom.removeEventListener(type, handle, capture);
    };
    attach();
    editor.on('mount', attach);
    return () => { editor.off('mount', attach); detach(); };
  }, [editor, type, capture, active]);
}
